/**
 * Pipeline Labs Lead Finder Page
 *
 * This component provides the interface for Pipeline Labs lead scraping.
 * Key differences from Peaky Leads:
 * - Uses camelCase field names for API compatibility
 * - Supports preview modal with masked PII
 * - Different filter structure (personTitleIncludes vs person_title)
 * - Email verification filter options
 */

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SubscriptionStatus from '../user/SubscriptionStatus';
import useSubscription from '../../hooks/useSubscription';
import { useSettings } from '../../hooks/useSettings';
import { resolveContentLayout } from '../../theme/presets';
import { Download, Info } from 'lucide-react';

// Import filter components
import PipelineLabsPersonFilters from './filters/PipelineLabsPersonFilters';
import PipelineLabsCompanyFilters from './filters/PipelineLabsCompanyFilters';
import PipelineLabsContactFilters from './filters/PipelineLabsContactFilters';

// Import filter constants
import {
  SENIORITY_LEVELS,
  JOB_FUNCTIONS,
  EMPLOYEE_SIZE_RANGES,
  EMAIL_STATUS_OPTIONS
} from '../../constants/pipelineLabsOptions';

const PipelineLabsLeadFinderPage = () => {
  // Modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // List name for job identification
  const [listName, setListName] = useState('');

  // Lead extraction settings
  const [extractAllLeads, setExtractAllLeads] = useState(true);
  const [customLeadCount, setCustomLeadCount] = useState('');

  // Filter state (camelCase to match Pipeline Labs API)
  const [filters, setFilters] = useState({
    personTitleIncludes: [],
    personTitleExcludes: [],
    includeSimilarTitles: false,
    personTitleExtraIncludes: [],
    seniorityIncludes: [],
    seniorityExcludes: [],
    personFunctionIncludes: [],
    personFunctionExcludes: [],
    personLocationCountryIncludes: [],
    personLocationCountryExcludes: [],
    personLocationStateIncludes: [],
    personLocationStateExcludes: [],
    personLocationCityIncludes: [],
    personLocationCityExcludes: [],
    companyNameIncludes: [],
    companyNameExcludes: [],
    companyNameMatchMode: 'phrase',
    companyDomainIncludes: [],
    companyDomainExcludes: [],
    companyDomainMatchMode: 'strict',
    companyEmployeeSizeIncludes: [],
    companyIndustryIncludes: [],
    companyIndustryExcludes: [],
    companyKeywordIncludes: [],
    companyKeywordExcludes: [],
    companyLocationCountryIncludes: [],
    companyLocationCountryExcludes: [],
    companyLocationStateIncludes: [],
    companyLocationStateExcludes: [],
    companyLocationCityIncludes: [],
    companyLocationCityExcludes: [],
    emailStatus: null,
    hasEmail: false,
    hasPhone: false,
    totalResults: 10000
  });

  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Job history state
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState(null);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Subscription state - using centralized hook for proper login-as support
  const { subscription, subscriptionLoading } = useSubscription();

  // Settings state - using centralized hook (same pattern as Dashboard)
  const { settings } = useSettings();
  const isFocused = resolveContentLayout(settings?.content_layout) === 'focused';

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Sorting and filtering state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [hoveredJobId, setHoveredJobId] = useState(null);

  // Client-side sorting and filtering
  const sortedAndFilteredJobs = useMemo(() => {
    let filtered = jobs;

    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = jobs.filter(job => (job.list_name || '').toLowerCase().includes(q));
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle date sorting
      if (sortBy === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      // Handle numeric sorting
      else if (sortBy === 'estimated_leads' || sortBy === 'result_count') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }
      // Handle string sorting
      else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [jobs, searchQuery, sortBy, sortDir]);

  // Clone job handler - prefills form with job's filters
  const handleCloneJob = (job) => {
    // Reset any previous state
    setError(null);
    setPreviewData(null);

    // Prefill list name with "Copy of {original name}"
    setListName(`Copy of ${job.list_name}`);

    // Prefill filters from the job's raw_filters (camelCase format)
    if (job.raw_filters) {
      const clonedFilters = {
        personTitleIncludes: job.raw_filters.personTitleIncludes || [],
        personTitleExcludes: job.raw_filters.personTitleExcludes || [],
        includeSimilarTitles: job.raw_filters.includeSimilarTitles || false,
        personTitleExtraIncludes: job.raw_filters.personTitleExtraIncludes || [],
        seniorityIncludes: job.raw_filters.seniorityIncludes || [],
        seniorityExcludes: job.raw_filters.seniorityExcludes || [],
        functionIncludes: job.raw_filters.functionIncludes || [],
        functionExcludes: job.raw_filters.functionExcludes || [],
        personLocationCityIncludes: job.raw_filters.personLocationCityIncludes || [],
        personLocationCountryIncludes: job.raw_filters.personLocationCountryIncludes || [],
        personLocationStateIncludes: job.raw_filters.personLocationStateIncludes || [],
        companyNameIncludes: job.raw_filters.companyNameIncludes || [],
        companyNameExcludes: job.raw_filters.companyNameExcludes || [],
        companyNameMatchMode: job.raw_filters.companyNameMatchMode || 'phrase',
        companyDomainIncludes: job.raw_filters.companyDomainIncludes || [],
        companyDomainExcludes: job.raw_filters.companyDomainExcludes || [],
        companyDomainMatchMode: job.raw_filters.companyDomainMatchMode || 'strict',
        companyEmployeeSizeIncludes: job.raw_filters.companyEmployeeSizeIncludes || [],
        companyEmployeeSizeExcludes: job.raw_filters.companyEmployeeSizeExcludes || [],
        companyIndustryIncludes: job.raw_filters.companyIndustryIncludes || [],
        companyIndustryExcludes: job.raw_filters.companyIndustryExcludes || [],
        companyKeywordIncludes: job.raw_filters.companyKeywordIncludes || [],
        companyKeywordExcludes: job.raw_filters.companyKeywordExcludes || [],
        companyLocationCountryIncludes: job.raw_filters.companyLocationCountryIncludes || [],
        companyLocationCountryExcludes: job.raw_filters.companyLocationCountryExcludes || [],
        companyLocationStateIncludes: job.raw_filters.companyLocationStateIncludes || [],
        companyLocationStateExcludes: job.raw_filters.companyLocationStateExcludes || [],
        companyLocationCityIncludes: job.raw_filters.companyLocationCityIncludes || [],
        companyLocationCityExcludes: job.raw_filters.companyLocationCityExcludes || [],
        emailStatus: job.raw_filters.emailStatus || null,
        hasEmail: job.raw_filters.hasEmail || false,
        hasPhone: job.raw_filters.hasPhone || false
      };
      setFilters(clonedFilters);
    }

    // Open the form modal
    setShowFormModal(true);
  };

  // Fetch jobs (using axios - same pattern as Dashboard)
  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);
      setJobsError(null);

      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/lead-finder/jobs?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 15000
      });

      setJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setJobsError(err.response?.data?.detail || err.message || 'Failed to fetch jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  // Download handler (using axios - same pattern as Dashboard)
  const handleDownload = async (jobId, listName) => {
    setDownloadingJobId(jobId);
    try {
      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/lead-finder/jobs/${jobId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'arraybuffer',
        validateStatus: () => true
      });

      if (response.status === 202) {
        const text = new TextDecoder('utf-8').decode(response.data);
        const data = JSON.parse(text);
        alert(data.message || 'Your file is being generated. Please check back in a few moments.');
        return;
      }

      if (response.status >= 400) {
        const text = new TextDecoder('utf-8').decode(response.data);
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Download failed');
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${listName || 'leads'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert(`Failed to download: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDownloadingJobId(null);
    }
  };

  // Clean filters before sending (remove empty arrays, false booleans, null values)
  const cleanFilters = (filters) => {
    const cleaned = {};

    for (const [key, value] of Object.entries(filters)) {
      // Include non-empty arrays
      if (Array.isArray(value) && value.length > 0) {
        cleaned[key] = value;
      }
      // Include true booleans
      else if (typeof value === 'boolean' && value === true) {
        cleaned[key] = value;
      }
      // Include non-empty strings
      else if (typeof value === 'string' && value.trim() !== '') {
        cleaned[key] = value;
      }
      // Include numbers (except 0 if it's meaningless)
      else if (typeof value === 'number' && value > 0) {
        cleaned[key] = value;
      }
    }

    return cleaned;
  };

  // Preview leads (using axios - same pattern as Dashboard)
  const handlePreview = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
      const cleanedFilters = cleanFilters(filters);

      // Debug: Log filters before and after cleaning
      console.log('[Pipeline Labs Preview] Raw filters:', filters);
      console.log('[Pipeline Labs Preview] Cleaned filters:', cleanedFilters);

      const response = await axios.post(`${API_BASE_URL}/api/lead-finder/preview`, cleanedFilters, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 30000
      });

      setPreviewData(response.data);
      setShowPreviewModal(true);
    } catch (err) {
      console.error('Preview error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  // Proceed to confirmation modal
  const handleProceedToConfirm = () => {
    setShowPreviewModal(false);
    setShowConfirmModal(true);
  };

  // Submit job (using axios - same pattern as Dashboard)
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
      const cleanedFilters = cleanFilters(filters);

      // Add totalResults based on user selection
      // When extractAllLeads is true, use the actual count from Pipeline Labs API (total_results)
      // When extracting a custom count, use the user-specified amount
      const finalFilters = {
        ...cleanedFilters,
        totalResults: extractAllLeads
          ? (previewData?.total_results || previewData?.count || 100)
          : parseInt(customLeadCount, 10) || 100
      };

      const response = await axios.post(`${API_BASE_URL}/api/lead-finder/jobs`, {
        list_name: listName,
        filters: finalFilters
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 30000
      });

      setSubmitSuccess(true);
      console.log('Job submitted successfully:', response.data);

      // Reset form and close modals after 2 seconds
      setTimeout(() => {
        setShowConfirmModal(false);
        setShowPreviewModal(false);
        setShowFormModal(false);
        handleResetFilters();
        fetchJobs(); // Refresh job list
      }, 2000);

    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch jobs on mount (subscription and settings handled by hooks)
  useEffect(() => {
    fetchJobs();
  }, []);

  // Reset form handler
  const handleResetFilters = () => {
    setListName('');
    setFilters({
      personTitleIncludes: [],
      personTitleExcludes: [],
      includeSimilarTitles: false,
      personTitleExtraIncludes: [],
      seniorityIncludes: [],
      seniorityExcludes: [],
      personFunctionIncludes: [],
      personFunctionExcludes: [],
      personLocationCountryIncludes: [],
      personLocationCountryExcludes: [],
      personLocationStateIncludes: [],
      personLocationStateExcludes: [],
      personLocationCityIncludes: [],
      personLocationCityExcludes: [],
      companyNameIncludes: [],
      companyNameExcludes: [],
      companyNameMatchMode: 'phrase',
      companyDomainIncludes: [],
      companyDomainExcludes: [],
      companyDomainMatchMode: 'strict',
      companyEmployeeSizeIncludes: [],
      companyIndustryIncludes: [],
      companyIndustryExcludes: [],
      companyKeywordIncludes: [],
      companyKeywordExcludes: [],
      companyLocationCountryIncludes: [],
      companyLocationCountryExcludes: [],
      companyLocationStateIncludes: [],
      companyLocationStateExcludes: [],
      companyLocationCityIncludes: [],
      companyLocationCityExcludes: [],
      emailStatus: null,
      hasEmail: false,
      hasPhone: false
    });
    setPreviewData(null);
    setError(null);
    setExtractAllLeads(true);
    setCustomLeadCount('');
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    handleResetFilters();
  };

  return (
    <div style={{
      padding: '24px',
      maxWidth: 1400,
      margin: '0 auto',
      minHeight: 'calc(100vh - 100px)'
    }}>
      {/* Top Row: CTA Card and Subscription Card side by side */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: 24,
        marginBottom: 24
      }}>
        {/* Call to Action Card */}
        <Card style={{
          background: 'linear-gradient(135deg, #faf5ff 0%, white 100%)',
          border: '1px solid #e9d5ff'
        }}>
          <Card.Content>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div>
                <h2 style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: 'var(--color-info-fg)',
                  margin: 0,
                  marginBottom: 8
                }}>
                  Ready to find qualified leads?
                </h2>
                <p style={{
                  color: 'var(--color-text-muted)',
                  margin: 0,
                  fontSize: 14
                }}>
                  Configure filters and discover prospects matching your ideal customer profile
                </p>
              </div>
              <Button onClick={() => setShowFormModal(true)}>
                🔍 Find New Leads
              </Button>
            </div>
          </Card.Content>
        </Card>

        {/* Subscription Status */}
        {!isFocused && (
<SubscriptionStatus
          subscription={subscription}
          subscriptionLoading={subscriptionLoading}
        />

        )}
      </div>

      {/* Lead Finder Jobs Table */}
      <Card>
        <Card.Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Card.Title>Lead Finder Jobs</Card.Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                placeholder="Search by list name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '8px 10px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  fontSize: 14
                }}
              />
              <Button
                variant="secondary"
                onClick={fetchJobs}
                disabled={loadingJobs}
              >
                Refresh
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Content style={{ padding: 0 }}>
          {loadingJobs ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '12px' }}>⏳</div>
              <div style={{ color: 'var(--color-text-muted)' }}>Loading job history...</div>
            </div>
          ) : jobsError ? (
            <div style={{
              background: 'var(--color-danger-bg)',
              border: '1px solid var(--color-danger-border)',
              borderRadius: '8px',
              padding: '20px',
              margin: '16px',
              color: 'var(--color-danger-fg)'
            }}>
              ❌ Failed to load jobs: {jobsError}
            </div>
          ) : jobs.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              border: '2px dashed var(--color-border)',
              margin: '16px',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--color-text-primary)'
              }}>
                No jobs yet
              </h3>
              <p style={{
                margin: '0 0 16px 0',
                fontSize: '14px',
                color: 'var(--color-text-muted)'
              }}>
                Click "Find New Leads" above to create your first Lead Finder job
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0
              }}>
                <thead>
                  <tr>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const nextDir = sortBy === 'created_at' && sortDir === 'desc' ? 'asc' : 'desc';
                      setSortBy('created_at');
                      setSortDir(nextDir);
                    }}>
                      Date {sortBy === 'created_at' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const nextDir = sortBy === 'list_name' && sortDir === 'desc' ? 'asc' : 'desc';
                      setSortBy('list_name');
                      setSortDir(nextDir);
                    }}>
                      List Name {sortBy === 'list_name' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const nextDir = sortBy === 'status' && sortDir === 'desc' ? 'asc' : 'desc';
                      setSortBy('status');
                      setSortDir(nextDir);
                    }}>
                      Status {sortBy === 'status' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const nextDir = sortBy === 'estimated_leads' && sortDir === 'desc' ? 'asc' : 'desc';
                      setSortBy('estimated_leads');
                      setSortDir(nextDir);
                    }}>
                      Requested {sortBy === 'estimated_leads' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const nextDir = sortBy === 'result_count' && sortDir === 'desc' ? 'asc' : 'desc';
                      setSortBy('result_count');
                      setSortDir(nextDir);
                    }}>
                      Extracted {sortBy === 'result_count' ? (sortDir === 'desc' ? '▼' : '▲') : ''}
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: 12,
                      color: 'var(--color-text-muted)'
                    }}>
                      Export Data
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAndFilteredJobs.map((job) => (
                    <tr key={job.id} style={{ borderBottom: '1px solid var(--color-bg-inset)' }}>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: 'var(--color-text-secondary)'
                      }}>
                        {new Date((job.created_at || '').includes('Z') ? job.created_at : job.created_at + 'Z').toLocaleString('en-GB', {
                          timeZone: (settings?.timezone || 'Asia/Kolkata'),
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: true
                        })}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 13,
                        color: 'var(--color-text-primary)',
                        fontWeight: 600
                      }}>
                        <button
                          onClick={() => handleCloneJob(job)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-info-hover)',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '13px',
                            fontWeight: 600,
                            padding: 0,
                            textAlign: 'left'
                          }}
                          onMouseEnter={(e) => e.target.style.color = 'var(--color-info-hover)'}
                          onMouseLeave={(e) => e.target.style.color = 'var(--color-info-hover)'}
                          title="Click to view job details"
                        >
                          {job.list_name}
                        </button>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative', zIndex: hoveredJobId === job.id ? 1001 : 'auto' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: 12,
                            background: job.status === 'completed' ? 'var(--color-success-bg)' : job.status === 'failed' ? 'var(--color-danger-bg)' : 'var(--color-warning-bg)',
                            color: job.status === 'completed' ? 'var(--color-success-fg)' : job.status === 'failed' ? 'var(--color-danger-hover)' : 'var(--color-warning-hover)',
                            border: '1px solid var(--color-border)'
                          }}>
                            {job.status}
                          </span>
                          {job.status === 'failed' && job.error_message && (
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'help',
                                color: 'var(--color-danger-hover)',
                                transition: 'transform 0.2s',
                                position: 'relative'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.1)';
                                setHoveredJobId(job.id);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                setHoveredJobId(null);
                              }}
                            >
                              <Info size={16} strokeWidth={2} />

                              {/* Custom Tooltip */}
                              {hoveredJobId === job.id && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    marginTop: '8px',
                                    backgroundColor: 'var(--color-text-primary)',
                                    color: '#ffffff',
                                    padding: '10px 14px',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    lineHeight: '1.5',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
                                    zIndex: 9999,
                                    minWidth: '200px',
                                    maxWidth: '350px',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    pointerEvents: 'none'
                                  }}
                                >
                                  <div style={{
                                    fontWeight: '600',
                                    marginBottom: '6px',
                                    color: '#fca5a5',
                                    fontSize: '12px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Failure Reason
                                  </div>
                                  {job.error_message}
                                  {/* Tooltip arrow */}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '100%',
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      width: 0,
                                      height: 0,
                                      borderLeft: '6px solid transparent',
                                      borderRight: '6px solid transparent',
                                      borderBottom: '6px solid var(--color-text-primary)'
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 13
                      }}>
                        {(job.estimated_leads || 0).toLocaleString()}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 13
                      }}>
                        {(job.result_count || 0).toLocaleString()}
                      </td>
                      <td style={{
                        padding: '10px 12px'
                      }}>
                        {job.status === 'completed' ? (
                          <Button
                            size="sm"
                            onClick={() => handleDownload(job.id, job.list_name)}
                            icon={<Download size={14} />}
                            loading={downloadingJobId === job.id}
                            disabled={downloadingJobId === job.id}
                          >
                            {downloadingJobId === job.id ? 'Preparing...' : 'Excel'}
                          </Button>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--color-text-disabled)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Content>
      </Card>

      {/* Lead Finder Form Modal */}
      {showFormModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '40px 20px',
          overflowY: 'auto'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: 'calc(100vh - 80px)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  margin: 0
                }}>
                  🔍 Configure Lead Finder
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--color-text-muted)',
                  margin: '4px 0 0 0'
                }}>
                  Set up filters to find your ideal prospects
                </p>
              </div>
              <button
                onClick={handleCloseFormModal}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                  padding: '8px',
                  fontSize: '24px',
                  lineHeight: 1,
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-inset)'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                ×
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px'
            }}>
              {/* List Name */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '8px'
                }}>
                  List Name <span style={{ color: 'var(--color-danger)' }}>*</span>
                </label>
                <input
                  type="text"
                  value={listName}
                  onChange={(e) => setListName(e.target.value)}
                  placeholder="e.g., Q1 2024 Enterprise Prospects"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--color-border-strong)',
                    borderRadius: '6px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--color-info)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--color-border-strong)'}
                />
                <p style={{
                  fontSize: '12px',
                  color: 'var(--color-text-muted)',
                  marginTop: '6px',
                  marginBottom: 0
                }}>
                  Give this lead list a unique name to identify it in your job history
                </p>
              </div>

              {/* Person Filters */}
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: 'var(--color-bg-subtle)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 16px 0'
                }}>
                  👤 Person Filters
                </h3>
                <PipelineLabsPersonFilters filters={filters} setFilters={setFilters} />
              </div>

              {/* Company Filters */}
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: 'var(--color-bg-subtle)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 16px 0'
                }}>
                  🏢 Company Filters
                </h3>
                <PipelineLabsCompanyFilters filters={filters} setFilters={setFilters} />
              </div>

              {/* Contact & System Filters */}
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: 'var(--color-bg-subtle)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 16px 0'
                }}>
                  📧 Contact & System Filters
                </h3>
                <PipelineLabsContactFilters filters={filters} setFilters={setFilters} />
              </div>

              {/* Error Display */}
              {error && (
                <div style={{
                  background: 'var(--color-danger-bg-strong)',
                  border: '1px solid var(--color-danger-border)',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--color-danger-fg)',
                      marginBottom: '4px'
                    }}>
                      Error
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: 'var(--color-danger-fg)'
                    }}>
                      {error}
                    </div>
                  </div>
                  <button
                    onClick={() => setError(null)}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: 'var(--color-danger-fg)',
                      cursor: 'pointer',
                      padding: '4px',
                      fontSize: '18px',
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Zero Results Warning */}
              {previewData && !error && previewData.count === 0 && (
                <div style={{
                  background: 'var(--color-warning-bg-strong)',
                  border: '1px solid var(--color-warning-border)',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>🔍</span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--color-warning-fg)',
                      marginBottom: '4px'
                    }}>
                      No Results Found
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: 'var(--color-warning-fg)'
                    }}>
                      We couldn't find any leads matching your current filters. Try adjusting your criteria or broadening your search.
                    </div>
                  </div>
                  <button
                    onClick={() => setPreviewData(null)}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: 'var(--color-warning-fg)',
                      cursor: 'pointer',
                      padding: '4px',
                      fontSize: '18px',
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Preview Count Display */}
              {previewData && !error && (previewData.count > 0 || previewData.total_results > 0) && (
                <>
                  <div style={{
                    background: 'var(--gradient-success)',
                    border: '2px solid var(--color-success-hover)',
                    borderRadius: '8px',
                    padding: '24px',
                    marginBottom: '20px',
                    boxShadow: '0 4px 12px rgba(5, 150, 105, 0.2)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px'
                    }}>
                      <span style={{ fontSize: '36px' }}>✅</span>
                      <div>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: 'white',
                          marginBottom: '4px'
                        }}>
                          Leads Available
                        </div>
                        <div style={{
                          fontSize: '32px',
                          fontWeight: '700',
                          color: 'white',
                          lineHeight: 1
                        }}>
                          {previewData.count?.toLocaleString()} leads
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Preview Results Table */}
                  {previewData.preview_results && previewData.preview_results.length > 0 && (
                    <div style={{
                      marginBottom: '20px',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        background: 'var(--color-bg-subtle)',
                        padding: '12px 16px',
                        borderBottom: '1px solid var(--color-border)'
                      }}>
                        <h4 style={{
                          margin: 0,
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--color-text-secondary)'
                        }}>
                          Sample Results (First {previewData.preview_results.length} leads)
                        </h4>
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '13px'
                        }}>
                          <thead>
                            <tr style={{ background: 'var(--color-bg-inset)' }}>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>FIRST NAME</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>LAST NAME</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>FULL NAME</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>EMAIL</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>PHONE</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>POSITION</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>CITY</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>STATE</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>COUNTRY</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>LINKEDIN</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>SENIORITY</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>FUNCTIONS</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>EMAIL STATUS</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>ORG NAME</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>ORG WEBSITE</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>ORG LINKEDIN</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>FOUNDED YEAR</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>INDUSTRY</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.preview_results.map((lead, index) => {
                              // Helper function to render found/not found status
                              const renderStatus = (value) => {
                                if (!value || value === '—') return '—';
                                const isFound = value.includes('✓') || (!value.includes('✗') && value !== 'Not Found');
                                const displayText = value.includes('✓') ? '✓ Found' : value.includes('✗') ? '✗ Not Found' : value;
                                return (
                                  <span style={{
                                    color: isFound ? 'var(--color-success-fg)' : 'var(--color-danger-hover)',
                                    fontWeight: '500'
                                  }}>
                                    {displayText}
                                  </span>
                                );
                              };

                              // Parse array strings like "['entrepreneurship']" to display properly
                              const parseArrayString = (str) => {
                                if (!str || str === '—') return '—';
                                try {
                                  // Remove extra quotes and brackets
                                  const cleaned = str.replace(/^\['|'\]$/g, '').replace(/'/g, '');
                                  return cleaned || '—';
                                } catch {
                                  return str;
                                }
                              };

                              return (
                                <tr key={index} style={{ borderBottom: '1px solid var(--color-bg-inset)' }}>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{lead.firstName || lead.first_name || '—'}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{lead.lastName || lead.last_name || '—'}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{lead.fullName || lead.full_name || lead.name || '—'}</td>
                                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{renderStatus(lead.email)}</td>
                                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{renderStatus(lead.phone)}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{lead.position || lead.title || '—'}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{lead.city || lead.person_city || lead.personCity || '—'}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{lead.state || lead.person_state || lead.personState || '—'}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{lead.country || lead.person_country || lead.personCountry || '—'}</td>
                                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{renderStatus(lead.linkedinUrl || lead.linkedin_url)}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{lead.seniority || '—'}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{parseArrayString(lead.functional || lead.functions || lead.function || lead.job_function || lead.jobFunction)}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                                    {lead.email_status || lead.emailStatus ? (
                                      <span style={{
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        background: (lead.email_status || lead.emailStatus) === 'verified' ? '#dcfce7' : 'var(--color-warning-bg-strong)',
                                        color: (lead.email_status || lead.emailStatus) === 'verified' ? 'var(--color-success-fg)' : 'var(--color-warning-hover)'
                                      }}>
                                        {lead.email_status || lead.emailStatus}
                                      </span>
                                    ) : '—'}
                                  </td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{lead.orgName || lead.organization_name || lead.organizationName || lead.company_name || lead.companyName || '—'}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                                    {lead.orgWebsite || lead.organization_website || lead.organizationWebsite || lead.company_website || lead.companyWebsite ? (
                                      <a href={lead.orgWebsite || lead.organization_website || lead.organizationWebsite || lead.company_website || lead.companyWebsite} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-info-hover)', textDecoration: 'underline' }}>Visit</a>
                                    ) : '—'}
                                  </td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                                    {lead.orgLinkedinUrl || lead.organization_linkedin_url || lead.organizationLinkedinUrl || lead.company_linkedin_url || lead.companyLinkedinUrl ? (
                                      <a href={lead.orgLinkedinUrl || lead.organization_linkedin_url || lead.organizationLinkedinUrl || lead.company_linkedin_url || lead.companyLinkedinUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-info-hover)', textDecoration: 'underline' }}>View</a>
                                    ) : '—'}
                                  </td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{lead.orgFoundedYear || lead.founded_year || lead.foundedYear || '—'}</td>
                                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{parseArrayString(lead.orgIndustry || lead.industry)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '20px 24px',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-bg-subtle)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <Button
                variant="secondary"
                onClick={handleResetFilters}
                style={{
                  color: 'var(--color-danger-hover)',
                  borderColor: '#fca5a5'
                }}
              >
                🔄 Reset Filters
              </Button>
              <Button
                variant="secondary"
                onClick={handlePreview}
                disabled={!listName.trim() || loading}
              >
                {loading ? '⏳ Checking...' : '🔢 Preview Results'}
              </Button>
              {previewData && !error && (previewData.count > 0 || previewData.total_results > 0) && (
                <Button
                  variant="primary"
                  onClick={handleProceedToConfirm}
                  disabled={!listName.trim()}
                  title={!listName.trim() ? 'Please enter a list name' : ''}
                >
                  Continue →
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Job Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%'
          }}>
            {!submitSuccess ? (
              <>
                <h3 style={{
                  marginTop: 0,
                  marginBottom: '16px',
                  fontSize: '20px',
                  fontWeight: '600'
                }}>
                  Confirm Lead Finder Job
                </h3>

                <div style={{
                  background: 'var(--color-bg-subtle)',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                      List Name
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-text-primary)' }}>
                      {listName}
                    </div>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                      Available Leads
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-text-primary)' }}>
                      {previewData?.total_results?.toLocaleString() || previewData?.count?.toLocaleString()} leads found
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '8px' }}>
                      How many leads do you want to extract?
                    </div>

                    {/* Extract All Option */}
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      border: `2px solid ${extractAllLeads ? 'var(--color-info-hover)' : 'var(--color-border)'}`,
                      borderRadius: '6px',
                      marginBottom: '8px',
                      cursor: 'pointer',
                      background: extractAllLeads ? 'var(--color-info-bg)' : 'white',
                      transition: 'all 0.2s'
                    }}>
                      <input
                        type="radio"
                        checked={extractAllLeads}
                        onChange={() => setExtractAllLeads(true)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                          accentColor: 'var(--color-info-hover)',
                          marginRight: '12px'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-text-primary)' }}>
                          Extract All ({previewData?.total_results?.toLocaleString() || previewData?.count?.toLocaleString()} leads)
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                          Get all available leads matching your filters
                        </div>
                      </div>
                    </label>

                    {/* Custom Number Option */}
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      border: `2px solid ${!extractAllLeads ? 'var(--color-info-hover)' : 'var(--color-border)'}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: !extractAllLeads ? 'var(--color-info-bg)' : 'white',
                      transition: 'all 0.2s'
                    }}>
                      <input
                        type="radio"
                        checked={!extractAllLeads}
                        onChange={() => setExtractAllLeads(false)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                          accentColor: 'var(--color-info-hover)',
                          marginRight: '12px'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-text-primary)', marginBottom: '8px' }}>
                          Extract Custom Number
                        </div>
                        <input
                          type="number"
                          min="1"
                          max={previewData?.total_results || previewData?.count || 50000}
                          value={customLeadCount}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue === '') {
                              setCustomLeadCount('');
                              return;
                            }
                            const val = parseInt(inputValue, 10);
                            const maxLeads = previewData?.total_results || previewData?.count || 50000;
                            if (!isNaN(val)) {
                              setCustomLeadCount(Math.min(Math.max(val, 1), maxLeads));
                            }
                          }}
                          disabled={extractAllLeads}
                          onClick={() => setExtractAllLeads(false)}
                          placeholder="Enter number of leads"
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            border: '1px solid var(--color-border-strong)',
                            borderRadius: '4px',
                            fontSize: '13px',
                            background: extractAllLeads ? 'var(--color-bg-inset)' : 'white',
                            cursor: extractAllLeads ? 'not-allowed' : 'text'
                          }}
                        />
                        <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                          Maximum: {(previewData?.total_results || previewData?.count || 50000).toLocaleString()} leads
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Credits Summary */}
                <div style={{
                  background: 'var(--color-success-bg)',
                  border: '1px solid #86efac',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--color-success-fg)' }}>
                    Credits Required:
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--color-success-fg)' }}>
                    {(extractAllLeads
                      ? (previewData?.total_results || previewData?.count || 0)
                      : customLeadCount
                    ).toLocaleString()} credits
                  </div>
                </div>

                {/* Credit adjustment notice */}
                <div style={{
                  background: 'var(--color-info-bg)',
                  border: '1px solid var(--color-info-border)',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: 'var(--color-info-fg)',
                  lineHeight: '1.5'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    ℹ️ Credit Adjustment Policy
                  </div>
                  This is an estimated lead count. If the actual extracted leads differ, we will automatically adjust your credits accordingly: refunding if fewer leads are found, or deducting additional credits if more leads are extracted.
                </div>

                {error && (
                  <div style={{
                    background: 'var(--color-danger-bg)',
                    border: '1px solid var(--color-danger-border)',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '16px',
                    color: 'var(--color-danger-fg)',
                    fontSize: '14px'
                  }}>
                    ❌ {error}
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowConfirmModal(false);
                      setShowPreviewModal(true);
                    }}
                    disabled={submitting}
                  >
                    ← Back
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={submitting || (!extractAllLeads && !customLeadCount)}
                  >
                    {submitting ? '⏳ Submitting...' : '🚀 Submit Job'}
                  </Button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                <h3 style={{
                  marginTop: 0,
                  marginBottom: '8px',
                  color: 'var(--color-success-hover)'
                }}>
                  Job Submitted Successfully!
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--color-text-muted)',
                  margin: 0
                }}>
                  Your leads are being collected. Check your job history for updates.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineLabsLeadFinderPage;
